import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  Users, Facebook, BarChart3, Settings, LogOut, Shield,
  Bot, MessageSquare, Menu, X, Crown, Shirt, ShoppingBag,
  Video, Wand2, FileText, Sparkles, Coins, Clock, Bell, Mic, Wrench, Activity
} from 'lucide-react';
import PageManagement from './PageManagement';
import UserManagement from './UserManagement';
import FacebookAppsManagement from './FacebookAppsManagement';
import OfficialPagesManagement from './OfficialPagesManagement';
import BotStudio from './BotStudio';
import CMStudio from './CMStudio';
import CreditOrders from './CreditOrders';
import ServiceRequests from './ServiceRequests';
import WhatsAppConnections from './WhatsAppConnections';
import ManyChatPagesManagement from './ManyChatPagesManagement';
import FashionGlowQuotas from './FashionGlowQuotas';
import AdminSubscriptions from './AdminSubscriptions';
import VideoAdsQuotas from './VideoAdsQuotas';
import MarketingStudioAdmin from './MarketingStudioAdmin';
import CVCreditsAdmin from './CVCreditsAdmin';   // ✅ NOUVEAU
import IAStudioVideoAdmin from './IAStudioVideoAdmin';
import VoiceSplitAdmin from './VoiceSplitAdmin';
import UtileAdmin from './UtileAdmin';
import IAStudioConsumptionStats from './IAStudioConsumptionStats';
import TrialManagement from './TrialManagement';
import { API_URL } from '../../config';
import AssistantTeckiAAdmin from './AssistantTeckiAAdmin';
import LLMPerformance from './LLMPerformance';
import ClientsCredits from './ClientsCredits';
import UserActivity from './UserActivity';
import AgentOffersAdmin from './AgentOffersAdmin';
import SupportAdmin from './SupportAdmin';
import StaffAdmin from './StaffAdmin';
import AdminMessaging from './AdminMessaging';


// Droits du compte connecté (Superadmin / agent à onglets limités) : le menu et les vues s'adaptent, le serveur applique la même règle.
const PermCtx = createContext({ can: () => true, groupVisible: () => true });

export default function AdminDashboard({ onLogout }) {
  const [view, setView]                           = useState('dashboard');
  const [focus, setFocus]                         = useState({ clientId: '', prefill: null, n: 0 });   // cible d'un clic « À CONFIGURER »
  const [access, setAccess]                       = useState(null);   // { role, is_super, perms, tabs, email }
  const [sidebarOpen, setSidebarOpen]             = useState(false);
  // Compteurs "en attente" pour les badges numeriques de la sidebar (un seul appel groupe,
  // voir /api/admin/pending-counts) - demande explicite de Hery le 2026-08-20.
  const [pendingCounts, setPendingCounts] = useState({
    subscriptions: 0, cm_credit_orders: 0, credit_orders: 0, ia_studio_video_orders: 0,
    ia_studio_image_orders: 0, service_requests: 0, trials_ending_soon: 0, voice_split_orders: 0, utile_orders: 0, motion_orders: 0, agent_orders: 0, agents_to_setup: 0, support_unread: 0,
  });
  const [stats, setStats] = useState({
    total_users: 0, active_users: 0,
    total_clients: 0, active_clients: 0,
    inactive_clients: 0, active_sessions: 0,
    monitored_posts: 0, total_comments: 0,
  });

  const can = (k) => {
    if (!access) return true;                       // droits pas encore chargés : le serveur reste le juge
    if (k === 'staff') return !!access.is_super;    // gestion de l'équipe : Superadmin uniquement
    return access.perms.includes('*') || access.perms.includes(k);
  };
  const groupVisible = (label) => {
    if (!access) return true;
    if (label === 'Utilisateurs' && access.is_super) return true;
    return access.tabs.some(t => t.group === label && can(t.key));
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/staff/me`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
        const d = await r.json();
        if (d.success) {
          setAccess(d);
          const ok = (k) => d.perms.includes('*') || d.perms.includes(k);
          if (!ok('dashboard')) { const first = d.tabs.find(t => ok(t.key)); setView(first ? first.key : 'denied'); }
        }
      } catch (e) { console.error('Droits admin indisponibles:', e); }
    })();
  }, []);

  useEffect(() => {
    loadStats();
    loadPendingCounts();
    // Rafraichit periodiquement pour que les badges restent a jour sans recharger la page.
    const interval = setInterval(loadPendingCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPendingCounts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/pending-counts`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setPendingCounts(data.counts);
      }
    } catch (error) {
      console.error('Error loading pending counts:', error);
    }
  };

  const handleNavClick = (newView) => {
    setView(newView);
    setSidebarOpen(false);
  };

  // « À CONFIGURER » : Page Facebook -> Bot Studio sur cette Page ; WhatsApp -> SendPulse Connexion, formulaire pré-rempli
  const openSetup = (row) => {
    if (row.channel === 'facebook_cm' && !row.client_id) {
      setFocus(f => ({ clientId: '', prefill: null, userId: row.user_id, n: f.n + 1 }));
      handleNavClick('pages');
    } else if (row.channel === 'whatsapp' && !row.client_id) {
      setFocus(f => ({ clientId: '', prefill: { owner_user_id: row.user_id, name: row.business_name || row.contact_name || row.user_name || '', whatsapp_number: row.contact_phone || '' }, n: f.n + 1 }));
      handleNavClick('whatsapp');
    } else {
      setFocus(f => ({ clientId: row.client_id || '', prefill: null, n: f.n + 1 }));
      handleNavClick(row.channel === 'whatsapp' ? 'whatsapp' : 'bot_studio');
    }
  };

  const safeView = (access && view !== 'denied' && !can(view)) ? 'denied' : view;

  return (
    <PermCtx.Provider value={{ can, groupVisible }}>
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-red-950 to-slate-950">

      {/* ── Mobile Header ── */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-black/40 backdrop-blur-xl border-b border-white/10 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white font-bold text-sm">Admin Panel</div>
            <div className="text-xs text-gray-400">TDI Digital</div>
          </div>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 bg-white/10 rounded-lg text-white"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* ── Sidebar ── */}
      <div className={`
        fixed top-0 left-0 h-full w-64 bg-black/40 backdrop-blur-xl border-r border-white/10 p-6 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="hidden lg:flex items-center gap-3 mb-10">
          <img src="/TeckiA-logo.gif" alt="Admin Panel" className="w-12 h-12 rounded-xl object-cover" />
          <div>
            <div className="text-white font-bold">Admin Panel</div>
            <div className="text-xs text-gray-400">{access?.is_super ? 'Superadmin' : access?.role === 'agent' ? `Agent · ${access.email || ''}` : 'Agent TeckiA'}</div>
          </div>
        </div>
        <div className="h-4 lg:hidden" />

        {/* Nav */}
        <nav className="space-y-1 flex-1 overflow-y-auto">
          <NavItem viewKey="dashboard" icon={BarChart3}    label="Dashboard"            active={view === 'dashboard'}           onClick={() => handleNavClick('dashboard')} />

          {/* ── Utilisateurs ── */}
          <SectionLabel label="Utilisateurs" />
          <NavItem viewKey="staff" icon={Shield}       label="Équipe & accès"        active={view === 'staff'}                onClick={() => handleNavClick('staff')} badge="SUPER" badgeColor="violet" />
          <NavItem viewKey="users" icon={Users}        label="Utilisateurs"          active={view === 'users'}                onClick={() => handleNavClick('users')} />
          <NavItem viewKey="admin_messaging" icon={Bell}         label="Messagerie"             active={view === 'admin_messaging'}      onClick={() => handleNavClick('admin_messaging')} badge="NEW" badgeColor="green" />
          <NavItem viewKey="clients_credits" icon={Coins}        label="Crédits clients"       active={view === 'clients_credits'}      onClick={() => handleNavClick('clients_credits')} badge="NEW" badgeColor="green" />
          <NavItem viewKey="support_admin"
            icon={Bell} label="Support clients" active={view === 'support_admin'}
            onClick={() => handleNavClick('support_admin')}
            badge={pendingCounts.support_unread > 0 ? pendingCounts.support_unread : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="user_activity" icon={Activity}     label="Activité utilisateurs" active={view === 'user_activity'}        onClick={() => handleNavClick('user_activity')} badge="NEW" badgeColor="green" />

          {/* ── Facebook ── */}
          <SectionLabel label="Facebook" />
          <NavItem viewKey="agent_offers"
            icon={Crown} label="Offres des agents IA" active={view === 'agent_offers'}
            onClick={() => handleNavClick('agent_offers')}
            badge={(pendingCounts.agent_orders || 0) + (pendingCounts.agents_to_setup || 0) > 0 ? (pendingCounts.agent_orders || 0) + (pendingCounts.agents_to_setup || 0) : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="official_pages" icon={Facebook}     label="Pages API Officielle"  active={view === 'official_pages'}       onClick={() => handleNavClick('official_pages')} badge="NEW" badgeColor="green" />
          <NavItem viewKey="pages" icon={Facebook}     label="Pages Facebook"        active={view === 'pages'}                onClick={() => handleNavClick('pages')} />
          <NavItem viewKey="apps" icon={Settings}     label="Apps Facebook"         active={view === 'apps'}                 onClick={() => handleNavClick('apps')} />
          <NavItem viewKey="bot_studio" icon={Bot}          label="Bot Studio"            active={view === 'bot_studio'}           onClick={() => handleNavClick('bot_studio')} badge="NEW" badgeColor="green" />
          <NavItem viewKey="trial_management"
            icon={Clock} label="Essai Gratuit 7 jours" active={view === 'trial_management'}
            onClick={() => handleNavClick('trial_management')}
            badge={pendingCounts.trials_ending_soon > 0 ? pendingCounts.trials_ending_soon : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="cm_studio"
            icon={Sparkles} label="CM Studio" active={view === 'cm_studio'}
            onClick={() => handleNavClick('cm_studio')}
            badge={pendingCounts.cm_credit_orders > 0 ? pendingCounts.cm_credit_orders : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="whatsapp" icon={MessageSquare} label="SendPulse Connexion"  active={view === 'whatsapp'}             onClick={() => handleNavClick('whatsapp')} badge="NEW" badgeColor="green" />
          <NavItem viewKey="service_requests"
            icon={Users} label="Anciennes demandes" active={view === 'service_requests'}
            onClick={() => handleNavClick('service_requests')}
            badge={pendingCounts.service_requests > 0 ? pendingCounts.service_requests : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="manychat" icon={MessageSquare} label="ManyChat Pages"       active={view === 'manychat'}             onClick={() => handleNavClick('manychat')} />
          <NavItem viewKey="assistant_admin" icon={Sparkles} label="TeckiA Assistant" active={view==='assistant_admin'} onClick={()=>handleNavClick('assistant_admin')} badge="IA" badgeColor="green"/>
          <NavItem viewKey="llm_performance" icon={BarChart3}    label="Performance LLM"       active={view === 'llm_performance'}      onClick={() => handleNavClick('llm_performance')} badge="NEW" badgeColor="green" />

          {/* ── Abonnements & Crédits ── */}
          <SectionLabel label="Abonnements & Crédits" />
          <NavItem viewKey="admin_subscriptions"
            icon={ShoppingBag}
            label="Abonnements"
            active={view === 'admin_subscriptions'}
            onClick={() => handleNavClick('admin_subscriptions')}
            badge={pendingCounts.subscriptions > 0 ? pendingCounts.subscriptions : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="credit_orders"
            icon={ShoppingBag}
            label="Commandes Crédits"
            active={view === 'credit_orders'}
            onClick={() => handleNavClick('credit_orders')}
            badge={pendingCounts.credit_orders > 0 ? pendingCounts.credit_orders : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="cv_credits"
            icon={FileText}
            label="Crédits CV IA"
            active={view === 'cv_credits'}
            onClick={() => handleNavClick('cv_credits')}
            badge="CV"
            badgeColor="violet"
          />
          <NavItem viewKey="ia_studio_video_admin"
            icon={Video}
            label="IA Studio Vidéo"
            active={view === 'ia_studio_video_admin'}
            onClick={() => handleNavClick('ia_studio_video_admin')}
            badge={(pendingCounts.ia_studio_video_orders + pendingCounts.ia_studio_image_orders) > 0
              ? pendingCounts.ia_studio_video_orders + pendingCounts.ia_studio_image_orders : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="voice_split_admin"
            icon={Mic}
            label="Voice Kar"
            active={view === 'voice_split_admin'}
            onClick={() => handleNavClick('voice_split_admin')}
            badge={pendingCounts.voice_split_orders > 0 ? pendingCounts.voice_split_orders : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="utile_admin"
            icon={Wrench}
            label="TeckiA Utile"
            active={view === 'utile_admin'}
            onClick={() => handleNavClick('utile_admin')}
            badge={(pendingCounts.utile_orders || 0) + (pendingCounts.motion_orders || 0) > 0 ? (pendingCounts.utile_orders || 0) + (pendingCounts.motion_orders || 0) : null}
            badgeColor="yellow"
          />
          <NavItem viewKey="ia_studio_consumption"
            icon={Coins}
            label="Consommation IA Studio"
            active={view === 'ia_studio_consumption'}
            onClick={() => handleNavClick('ia_studio_consumption')}
            badge="Ar"
            badgeColor="green"
          />

          {/* ── Quotas services ── */}
          <SectionLabel label="Quotas Services" />
          <NavItem viewKey="video_ads_quotas" icon={Video}        label="Video Ads Quotas"      active={view === 'video_ads_quotas'}     onClick={() => handleNavClick('video_ads_quotas')}     badge="PRO" badgeColor="green" />
          <NavItem viewKey="marketing_studio_admin" icon={Wand2}        label="Marketing Studio"      active={view === 'marketing_studio_admin'} onClick={() => handleNavClick('marketing_studio_admin')} badge="PRO" badgeColor="green" />
          <NavItem viewKey="fashionglow" icon={Shirt}        label="Marketing Quotas"      active={view === 'fashionglow'}          onClick={() => handleNavClick('fashionglow')} />
        </nav>

        {/* Déconnexion */}
        <button
          onClick={onLogout}
          className="mt-6 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-300 transition-all w-full"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Déconnexion</span>
        </button>
      </div>

      {/* Overlay mobile */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Main Content ── */}
      <div className={`lg:ml-64 pt-16 lg:pt-0 ${['users','video_ads_quotas','marketing_studio_admin','clients_credits','user_activity','agent_offers','support_admin'].includes(view) ? 'p-0 overflow-hidden' : 'p-4 md:p-6 lg:p-8'}`}>

        {/* ─── DASHBOARD ──────────────────────────────────────────────────── */}
        {safeView === 'dashboard' && (
          <div className="space-y-6 md:space-y-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">Dashboard Admin</h1>
              <p className="text-sm md:text-base text-blue-200">Vue d'ensemble du système</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              <StatCard title="Utilisateurs"    value={stats.total_users}    subtitle={`${stats.active_users} actifs`}          icon={Users}    color="from-blue-600 to-cyan-600" />
              <StatCard title="Pages Facebook"  value={stats.total_clients}  subtitle={`${stats.active_clients} actives`}        icon={Facebook} color="from-purple-600 to-pink-600" />
              <StatCard title="Commentaires"    value={stats.total_comments} subtitle="Total système"                            icon={BarChart3} color="from-green-600 to-emerald-600" />
            </div>

            {/* Alerte abonnements en attente */}
            {pendingCounts.subscriptions > 0 && (
              <div className="bg-gradient-to-br from-yellow-600 to-orange-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white animate-pulse">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                  <ShoppingBag className="w-10 h-10 md:w-12 md:h-12" />
                  <div className="flex-1">
                    <h3 className="text-xl md:text-2xl font-bold mb-1">
                      {pendingCounts.subscriptions} Abonnement{pendingCounts.subscriptions > 1 ? 's' : ''} en attente
                    </h3>
                    <p className="text-sm md:text-base text-white/80">Configuration requise pour les nouveaux services</p>
                  </div>
                </div>
                <button onClick={() => handleNavClick('admin_subscriptions')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                  Voir les abonnements →
                </button>
              </div>
            )}

            {/* ── Card CV Crédits ── */}
            <div className="bg-gradient-to-br from-violet-700 to-blue-700 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <FileText className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">Crédits CV IA</h3>
                  <p className="text-sm md:text-base text-white/80">
                    Gérez les crédits de génération de CV par utilisateur · 1 crédit = 1 500 Ar
                  </p>
                </div>
              </div>
              <button onClick={() => handleNavClick('cv_credits')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer les crédits CV →
              </button>
            </div>

            {/* ── Card Video Ads ── */}
            <div className="bg-gradient-to-br from-pink-600 to-orange-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <Video className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">TeckiA Video Ads Quotas</h3>
                  <p className="text-sm md:text-base text-white/80">Gérez les quotas vidéo Standard PRO & Grok Imagine par utilisateur</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('video_ads_quotas')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer les quotas →
              </button>
            </div>

            {/* ── Card ManyChat ── */}
            <div className="bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <MessageSquare className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">ManyChat Bot Multi-Webhook</h3>
                  <p className="text-sm md:text-base text-white/80">Gérez vos bots ManyChat avec webhook unique par page</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('manychat')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Gérer ManyChat →
              </button>
            </div>

            {/* ── Card Messenger ── */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl md:rounded-2xl p-4 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <Bot className="w-10 h-10 md:w-12 md:h-12" />
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold mb-1">Bot Studio</h3>
                  <p className="text-sm md:text-base text-white/80">Configurez les agents IA de vos clients — identité, produits, documents OCR, ventes</p>
                </div>
              </div>
              <button onClick={() => handleNavClick('bot_studio')} className="w-full sm:w-auto px-6 py-3 rounded-xl bg-white/20 hover:bg-white/30 font-medium transition-all">
                Ouvrir Bot Studio →
              </button>
            </div>
          </div>
        )}

        {/* ─── VUES ──────────────────────────────────────────────────────── */}
        {safeView === 'users'                   && <UserManagement />}
        {safeView === 'clients_credits'         && <ClientsCredits />}
        {safeView === 'user_activity'           && <UserActivity />}
        {safeView === 'agent_offers'            && <AgentOffersAdmin onOpenSetup={openSetup} isSuper={!!access?.is_super} />}
        {safeView === 'support_admin'           && <SupportAdmin />}
        {safeView === 'admin_messaging'         && <AdminMessaging />}
        {safeView === 'pages'                   && <PageManagement key={focus.n} initialOauthUserId={focus.userId || ''} />}
        {safeView === 'official_pages'          && <OfficialPagesManagement />}
        {safeView === 'bot_studio'              && <BotStudio key={focus.n} initialClientId={focus.clientId} />}
        {safeView === 'trial_management'        && <TrialManagement />}
        {safeView === 'cm_studio'               && <CMStudio />}
        {safeView === 'whatsapp'                && <WhatsAppConnections key={focus.n} prefill={focus.prefill} />}
        {safeView === 'llm_performance'         && <LLMPerformance />}
        {safeView === 'service_requests'        && <ServiceRequests />}
        {safeView === 'credit_orders'           && <CreditOrders />}
        {safeView === 'apps'                    && <FacebookAppsManagement />}
        {safeView === 'manychat'                && <ManyChatPagesManagement />}
        {safeView === 'fashionglow'             && <FashionGlowQuotas />}
        {safeView === 'admin_subscriptions'     && <AdminSubscriptions />}
        {safeView === 'video_ads_quotas'        && <VideoAdsQuotas />}
        {safeView === 'marketing_studio_admin'  && <MarketingStudioAdmin />}
        {safeView === 'assistant_admin' && <AssistantTeckiAAdmin />}
        {safeView === 'cv_credits'              && <CVCreditsAdmin />}   {/* ✅ NOUVEAU */}
        {safeView === 'ia_studio_video_admin'   && <IAStudioVideoAdmin />}
        {safeView === 'voice_split_admin'       && <VoiceSplitAdmin />}
        {safeView === 'utile_admin'             && <UtileAdmin />}
        {safeView === 'ia_studio_consumption'   && <IAStudioConsumptionStats />}
        {safeView === 'staff'                && <StaffAdmin />}
        {safeView === 'denied' && (
          <div className="max-w-lg mx-auto mt-16 text-center bg-white/5 border border-white/10 rounded-3xl p-8">
            <Shield className="w-12 h-12 text-amber-300 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-1">Accès non autorisé</h2>
            <p className="text-sm text-gray-300">Votre compte n'a pas accès à cette section. Choisissez un onglet dans le menu, ou demandez l'accès au Superadmin.</p>
          </div>
        )}
      </div>
    </div>
    </PermCtx.Provider>
  );
}

// ── Composants helpers ────────────────────────────────────────────────────────

const SectionLabel = ({ label }) => {
  const { groupVisible } = useContext(PermCtx);
  if (!groupVisible(label)) return null;
  return (
    <div className="px-4 pt-4 pb-1">
      <span className="text-gray-500 text-xs font-semibold uppercase tracking-wider">{label}</span>
    </div>
  );
};

const NavItem = ({ icon: Icon, label, active, onClick, badge, badgeColor = 'green', viewKey }) => {
  const { can } = useContext(PermCtx);
  if (viewKey && !can(viewKey)) return null;
  const badgeColors = {
    green:  'bg-green-500',
    yellow: 'bg-yellow-500',
    red:    'bg-red-500',
    violet: 'bg-violet-500',
  };
  // Les badges texte statiques (NEW/AUTO/HUMAIN/CV/PRO/IA...) sont du bruit visuel permanent -
  // on ne les affiche qu'au survol. Les badges numeriques (compteurs "a valider") restent
  // toujours visibles : ce sont de vraies alertes actionnables, pas des etiquettes decoratives.
  const isNumeric = typeof badge === 'number';
  return (
    <button
      onClick={onClick}
      className={`group w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all relative text-left ${
        active ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
      }`}
    >
      <Icon className="w-5 h-5 flex-shrink-0" />
      <span className="font-medium text-sm truncate">{label}</span>
      {badge && (
        <span className={`absolute right-2 top-1/2 -translate-y-1/2 px-2 py-0.5 ${badgeColors[badgeColor]} text-white text-xs font-bold rounded-full transition-opacity ${
          isNumeric ? (badge > 0 ? 'animate-pulse' : '') : 'opacity-0 group-hover:opacity-100'
        }`}>
          {badge}
        </span>
      )}
    </button>
  );
};

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl md:rounded-2xl p-4 md:p-6 text-white`}>
    <div className="flex items-center justify-between mb-3 md:mb-4">
      <Icon className="w-8 h-8 md:w-10 md:h-10 opacity-80" />
      <div className="text-3xl md:text-4xl font-bold">{value}</div>
    </div>
    <h3 className="text-base md:text-lg font-semibold mb-1">{title}</h3>
    <p className="text-white/80 text-xs md:text-sm">{subtitle}</p>
  </div>
);